import React, { createContext, useContext, useReducer } from 'react';
import { AppContext, AppContextProvider } from '../../AppContext';
import roles from '../../helpers/roles';
import { addFormHeader } from '../../services';
import FORM_HEADERS from '../../enums/formHeaders';

export const UPDATE_IMAGES = 'UPDATE_IMAGES';

// Form headers that stay in state (so their images still show in All Photos, tagging, etc.)
// but are not shown as a tab or reachable via next/previous for the current user.
export const isFormHeaderHidden = (formHeader, state) =>
  !state.isAdmin && formHeader.formHeaderId === FORM_HEADERS.UNDERWRITING_ACTION;

export const getNextVisibleFormHeaderIndex = (state, fromIndex, step) => {
  for (let i = fromIndex + step; i >= 0 && i < state.formHeaders.length; i += step) {
    if (!isFormHeaderHidden(state.formHeaders[i], state)) {
      return i;
    }
  }
  return -1;
};

const findAndUpdateFormHeader = (formHeaders, { questionId, orderFormHeaderId, newAnswers }) => {
  formHeaders.forEach(fh => {
    if (fh.formDefinitions) {
      fh.formDefinitions = fh.formDefinitions.map(fd => {
        if (fd.questionId === questionId && fh.orderFormHeaderId === orderFormHeaderId) {
          fd.answers = newAnswers;
        }
        return fd;
      });
    }
  });
};

const updateFormHeaderAnswers = (state, action) => {
  let updatedFormHeaders = JSON.parse(JSON.stringify(state.formHeaders));

  if (action.updates) {
    action.updates.forEach(update => {
      findAndUpdateFormHeader(updatedFormHeaders, update);
    });
  } else {
    findAndUpdateFormHeader(updatedFormHeaders, action);
  }

  return updatedFormHeaders;
};

const updateImage = (state, action) => {
  let updatedFormHeaders = JSON.parse(JSON.stringify(state.formHeaders));
  updatedFormHeaders.forEach(fh => {
    if (fh.formDefinitions && fh.images) {
      fh.formDefinitions = fh.formDefinitions.map(fd => {
        fd.answers = fd.answers.map(ans => {
          if (ans.images && ans.images.length) {
            ans.images = ans.images.map(i => (i.formQaImageId === action.image.formQaImageId ? action.image : i));
          }
          return ans;
        });
        return fd;
      });
      fh.images = fh.images.map(i => {
        if (i.formQaImageId === action.image.formQaImageId) {
          return action.image;
        } else {
          return i;
        }
      });
    }
  });
  state.formHeaders = updatedFormHeaders;
  return state.formHeaders;
};

const updateImages = (state, action) => {
  let updatedFormHeaders = JSON.parse(JSON.stringify(state.formHeaders));
  let newImages = JSON.parse(JSON.stringify(action.images));

  newImages.forEach(image => {
    image.formQaImageId = image.formFieldRepImageId;
    image.hideImage = false;
    image.rotateDegree = 0;

    updatedFormHeaders.map(formHeader => {
      if (formHeader.orderFormHeaderId == image.orderFormHeaderId) {
        formHeader.images.push(image);
      }
    });
  });

  return updatedFormHeaders;
};

const addFormHeaderImage = (state, action) => {
  let updatedFormHeaders = JSON.parse(JSON.stringify(state.formHeaders));
  updatedFormHeaders.forEach(fh => {
    if (fh.images && fh.orderFormHeaderId === action.image.orderFormHeaderId) {
      fh.images = [...fh.images, action.image];
    }
  });
  state.formHeaders = updatedFormHeaders;
  return state.formHeaders;
};

const createNewFormDefinitionAnswer = (fd, image) => {
  const newAnswer = {
    orderFormHeaderId: fd.orderFormHeaderId,
    formDefinitionId: fd.formDefinitionId,
    questionId: fd.questionId,
    formQaAnswerId: null,
    type: fd.answerType,
    valueId: null,
    value: null,
    text: null,
    modifiedOn: new Date(),
    images: [image],
    default: false,
  };
  return newAnswer;
};

export const updateFormHeaderImageLocations = (state, action) => {
  // Make copy of active image
  const updatedImage = action.image;

  // Collect the answers for each of the selected location form definitions
  let selectedAnswers = [];
  action.selectedLocationFormDefinitions.forEach(fd => {
    selectedAnswers = selectedAnswers.concat(fd.answers);

    // If the selected location form definition does not have any answers, add one
    if (fd.answers.length === 0) {
      const newAnswer = createNewFormDefinitionAnswer(fd, updatedImage);
      selectedAnswers.push(newAnswer);
    }
  });

  // Add the active image to answer images for each of the selected location form definitions
  selectedAnswers = selectedAnswers.map(a => {
    let updatedAnswer = a;
    const exists = a.images.some(i => i.formQaImageId === updatedImage.formQaImageId);

    if (!exists) {
      updatedAnswer.images.push(updatedImage);
    }
    return updatedAnswer;
  });

  // Update form headers state to mirror the new location changes
  let updatedFormHeaders = JSON.parse(JSON.stringify(state.formHeaders));

  updatedFormHeaders.forEach(fh => {
    if (fh.formDefinitions) {
      fh.formDefinitions = fh.formDefinitions.map(fd => {
        if (fd.questionCategoryId !== 1) {
          return fd;
        }

        if (fd.answers.length > 0) {
          fd.answers = fd.answers
            .map(a => {
              a.images = a.images.filter(
                i =>
                  i.formQaImageId !== updatedImage.formQaImageId ||
                  (i.formQaImageId === updatedImage.formQaImageId &&
                    selectedAnswers.some(
                      sa => sa.orderFormHeaderId === a.orderFormHeaderId && sa.formDefinitionId === a.formDefinitionId,
                    )),
              );

              return a;
            })
            .filter(a => a.images.length > 0);
        } else {
          fd.answers = selectedAnswers.filter(
            sa => sa.orderFormHeaderId == fd.orderFormHeaderId && sa.formDefinitionId == fd.formDefinitionId,
          );
        }
        return fd;
      });
    }
  });

  return {
    formHeaders: updatedFormHeaders,
    answers: selectedAnswers,
  };
};

const removeOrderFormHeader = (state, action) => {
  let updatedFormHeaders = JSON.parse(JSON.stringify(state.formHeaders));
  state.formHeaders = updatedFormHeaders.filter(fh => fh.orderFormHeaderId !== action.orderFormHeaderId);
  return state.formHeaders;
};

const addOrderFormHeader = (state, action) => {
  let updatedFormHeaders = JSON.parse(JSON.stringify(state.formHeaders));
  //updatedFormHeaders.push(action.newOrderFormHeader);

  let existingOrderFormHeaders = updatedFormHeaders.filter(fh => fh.name == action.newOrderFormHeader.name);

  if (existingOrderFormHeaders.length == 1) {
    existingOrderFormHeaders[0].duplicateOrder = 1;
    action.newOrderFormHeader.duplicateOrder = 2;
  } else if (existingOrderFormHeaders.length > 1) {
    action.newOrderFormHeader.duplicateOrder = existingOrderFormHeaders.length + 1;
  }

  let insertAtIndex = 1;

  if (existingOrderFormHeaders.length > 0) {
    insertAtIndex =
      updatedFormHeaders.findIndex(
        fh =>
          fh.orderFormHeaderId == existingOrderFormHeaders[existingOrderFormHeaders.length - 1].orderFormHeaderId &&
          fh.formHeaderId == action.newOrderFormHeader.formHeaderId,
      ) + 1;
  } else {
    let primaryFormIndex = updatedFormHeaders.findIndex(fh => fh.name == 'Residential Primary');
    if (primaryFormIndex > 0) {
      insertAtIndex = primaryFormIndex;
    } else {
      insertAtIndex = updatedFormHeaders.findIndex(fh => fh.name == 'All Photos');
    }
  }
  updatedFormHeaders.splice(insertAtIndex, 0, action.newOrderFormHeader);

  state.formHeaders = updatedFormHeaders;
  return state.formHeaders;
};

let reducer = (state, action) => {
  switch (action.type) {
    case 'UPDATE_ANSWERS':
      return {
        ...state,
        formHeaders: updateFormHeaderAnswers(state, action),
      };
    case 'UPDATE_IMAGE':
      return {
        ...state,
        formHeaders: updateImage(state, action),
      };
    case 'UPDATE_IMAGES':
      return {
        ...state,
        formHeaders: updateImages(state, action),
      };
    case 'ADD_IMAGE':
      return {
        ...state,
        formHeaders: addFormHeaderImage(state, action),
      };
    case 'OVERRIDE_FORM_HEADERS':
      return {
        ...state,
        formHeaders: action.formHeaders,
      };
    // case 'UPDATE_IMAGE_LOCATIONS':
    //   return {
    //     ...state,
    //     formHeaders: updateFormHeaderImageLocations(state, action),
    //   };
    case 'CHANGE_FORM_HEADER':
      return {
        ...state,
        activeFormHeaderIndex: action.FormHeaderIndex,
      };
    case 'IS_AUTO_SAVING':
      return {
        ...state,
        isAutoSaving: action.value,
      };
    case 'SHOW_ALL_ANSWERS':
      return {
        ...state,
        showAllAnswers: action.value,
      };
    case 'UPDATE_FORM_HEADERS':
      const customFormHeaders = state.formHeaders.filter(
        sfh => !action.formHeaders.map(afh => afh.formHeaderId).includes(sfh.formHeaderId),
      );
      return {
        ...state,
        formHeaders: [...action.formHeaders, ...customFormHeaders],
      };
    case 'UPDATE_IS_CANCELED':
      return {
        ...state,
        isCancelled: action.isCancelled,
      };
    case 'UPDATE_IS_VALUATION_COMPLETE':
      return {
        ...state,
        isValuationComplete: action.isValuationComplete,
        isValuationOverride: action.isValuationOverride,
      };
    case 'IS_EDITING':
      return {
        ...state,
        isEditing: action.value,
      };
    case 'UPDATE_VALUATION_HISTORY':
      return {
        ...state,
        valuationHistory: action.valuationHistory,
      };
    case 'UPDATE_ORDER_FORM_HEADER_TO_REMOVE':
      console.log('UPDATE_ORDER_FORM_HEADER_TO_REMOVE', action);

      return {
        ...state,
        orderFormHeaderToRemove: action.orderFormHeader,
      };
    case 'REMOVE_ORDER_FORM_HEADER_SUCCESS':
      return {
        ...state,
        formHeaders: removeOrderFormHeader(state, action),
      };
    case 'ADD_ORDER_FORM_HEADER':
      return {
        ...state,
        formHeaders: addOrderFormHeader(state, action),
      };
    default:
      return;
  }
};

const OrderReviewContext = createContext();

function OrderReviewProvider(props) {
  const { dispatch: appDispatch } = useContext(AppContext);

  const {
    data,
    order,
    getOrderInfo,
    formHeaders,
    isCancelled,
    isValuationComplete,
    isValuationOverride,
    setIsValuationOverride,
    setIsValuationComplete,
    hazardHubData,
    vexcelPropertyData,
    remoteInvisionData,
    orderRecommendationData,
    valuationHistory,
    ausumOrLc360Links,
    isAusumOrLc360,
    roleAssignments,
    orderableFormHeaders,
  } = props;

  const initialState = {
    orderLineId: data.orderLineId,
    orderLineStatusLookupId: order.orderLineStatusLookupId,
    policy: order.policy,
    serviceStatus: data.serviceStatus,
    activeFormHeaderIndex: 0,
    formHeaders,
    orderInfoFormDefinitions: data.orderInfoFormDefinitions,
    isAutoSaving: false,
    showAllAnswers: true,
    isCancelled: isCancelled,
    //isValuationComplete: isValuationComplete,
    //isValuationOverride: isValuationOverride,
    isEditing: false,
    hazardHubData,
    vexcelPropertyData,
    remoteInvisionData,
    orderRecommendationData,
    valuationHistory,
    isAusumOrLc360,
    ausumOrLc360Links,
    isAusumOrLc360,
    orderFormHeaderToRemove: null,
    content: data.content,
    isRCT: data.isRCT,
    clientId: data.clientId,
    desktopUsedByFr: data.desktopUsedByFr,
    order,
    getOrderInfo,
    orderableFormHeaders,
  };

  const [reducerState, dispatch] = useReducer(reducer, initialState);
  const state = {
    ...reducerState,
    ...roleAssignments,
    isValuationOverride,
    isValuationComplete,
  };

  const addSupplementAsync = async formHeaderId => {
    try {
      // setSubmitting(true);
      const newOrderFormHeader = await addFormHeader(state.orderLineId, formHeaderId);

      dispatch({
        type: 'ADD_ORDER_FORM_HEADER',
        newOrderFormHeader: newOrderFormHeader,
      });

      // show success message
      appDispatch({
        type: 'SET_SNACKBAR',
        snackbar: {
          isOpen: true,
          message: 'Supplement added!',
          variant: 'success',
        },
      });

      getOrderInfo && getOrderInfo();

      return false;
    } catch (error) {
      // show error message
      const errorMessage = error.message || 'Failed to add supplement';

      appDispatch({
        type: 'SET_SNACKBAR',
        snackbar: {
          isOpen: true,
          message: errorMessage,
          variant: 'error',
        },
      });

      return errorMessage;
    }
  };

  const actions = { setIsValuationOverride, setIsValuationComplete, addSupplementAsync };

  return (
    <OrderReviewContext.Provider value={{ state, dispatch, actions }}>{props.children}</OrderReviewContext.Provider>
  );
}
export { OrderReviewContext, OrderReviewProvider };
